/**
 * Where a card came from, in a line: what sort of table it is, who made it,
 * and when. "Tool List" sat beside "Tools" with different numbers and nothing
 * to say it was a filtered copy someone took from a noticing.
 *
 * Read off what the server stores: `created_by` is "default" (set up with the
 * room), "user" (added by a person - and, on rooms older than "default", the
 * starting tables too), "interview" (the room's chat), "director" (a noticing
 * someone put on the board), or "human" (a person's brand card).
 */
import type { BoardroomDeliverable } from '../../../types';

const WHO: Record<string, string> = {
  default: 'set up with the room',
  user: 'added by you',
  human: 'added by you',
  interview: 'from the chat',
  director: 'from a noticing',
};

export function shortDate(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

const SUBJECT: Record<string, string> = {
  COMPANY: 'Company',
  PRODUCT: 'Product',
  SOLUTION_TYPE: 'Solution type',
  PAIN: 'Pain',
  FEATURE: 'Feature',
  IDEA: 'Idea',
  PRICING: 'Plan',
};

/** A kept page's tag - "Company", "Product" - or '' for every other card. So
 *  a company and its product of the same name do not read as one thing twice. */
export function subjectTag(d: Pick<BoardroomDeliverable, 'kind' | 'subject_kind'>): string {
  if (d.kind !== 'profile' || !d.subject_kind) return '';
  const k = d.subject_kind.toUpperCase();
  return SUBJECT[k] ?? k.charAt(0) + k.slice(1).toLowerCase().replace(/_/g, ' ');
}

/** What sort of card it is. `titleOf` names the table a filtered copy is of. */
export function cardSort(d: BoardroomDeliverable, titleOf: (kind: string) => string): string {
  if (d.kind === 'list' || d.kind === 'report') return d.kind === 'report' ? 'Report built by the chat' : 'Table built by the chat';
  if (d.kind === 'profile') return 'Page for one item';
  if (d.kind === 'brand') return 'Brand searches for one company';
  if (d.kind === 'note') return 'Note';
  if (!isBuiltIn(d)) return `Filtered copy of ${titleOf(d.kind)}`;
  return 'Built-in table';
}

/** The whole line: "Filtered copy of Tools · from a noticing · Oct 5". */
export function provenance(
  d: BoardroomDeliverable,
  titleOf: (kind: string) => string,
  now: Date = new Date(),
): string {
  const sort = cardSort(d, titleOf);
  // "Built by the chat" already says who.
  const who = d.kind === 'list' || d.kind === 'report' ? '' : WHO[d.created_by] ?? '';
  return [sort, who, shortDate(d.created_at, now)].filter(Boolean).join(' · ');
}

/** WHO MADE THE TABLE ITSELF, as a short tag with its own colour, for the
 *  card's corner. A built-in table is built in whoever pinned it - it is there
 *  from the start, on the shelf. Anything else (a filtered copy, a table the
 *  chat built, a page, a note) was made by someone, and `created_by` says who.
 *  Full class strings, so Tailwind keeps them. An unknown maker gets no tag. */
const BUILT_IN = { label: 'Built in', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };
const YOU = {
  label: 'You',
  className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
};
const MADE_BY: Record<string, { label: string; className: string }> = {
  user: YOU,
  human: YOU,
  interview: { label: 'Chat', className: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200' },
  director: { label: 'Noticed', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
};

export function madeBy(
  d: Pick<BoardroomDeliverable, 'kind' | 'subject' | 'created_by'>,
): { label: string; className: string } | null {
  if (isBuiltIn(d)) return BUILT_IN;
  return MADE_BY[d.created_by] ?? null;
}

function isBuiltIn(d: Pick<BoardroomDeliverable, 'kind' | 'subject'>): boolean {
  return !['list', 'report', 'profile', 'brand', 'note'].includes(d.kind) && !d.subject;
}

/** The name a filtered copy's parent goes by on this board. */
export function titleOfKind(deliverables: BoardroomDeliverable[], fallback: (kind: string) => string = k => k) {
  return (kind: string) =>
    deliverables.find(x => x.kind === kind && !x.subject && x.status !== 'dropped')?.title ?? fallback(kind);
}
